// AboneliklerSayfasi.tsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Autocomplete,
  InputLabel, FormControl, FormControlLabel, Switch, IconButton, Divider, CircularProgress, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const SubscriptionPage = () => {
  const [open, setOpen] = useState(false);
  const [selectedSubDoc, setSelectedSubDoc] = useState(null);
  const [hasQuotaChanges, setHasQuotaChanges] = useState(false);
  // Modal (ayrı pencere) listesi
  const [selectedItems, setSelectedItems] = useState([]);

  // Edit/View içi panel listesi
  const [quotaItems, setQuotaItems] = useState([]);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaOriginal, setQuotaOriginal] = useState([]);
  const [formData, setFormData] = useState({
    avatar: '',
    fullName: '',
    subscriptions: [
      { packageName: '', startDate: new Date().toISOString().split('T')[0], endDate: '' }
    ],
    isActive: false,
    paymentStatus: '',
    paymentType: '',
    paymentDate: '',
    campaignUsed: false,
    campaignId: '',
    campaignName: '',
  });

  const [abonelikler, setAbonelikler] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [, setCampaigns] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  const numeric = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));

  const buildQuotaDiffs = (current, original) => {
    const byId = Object.fromEntries(original.map(o => [String(o._id), o]));
    const keys = ['limitType', 'limitValue', 'usedCount', 'usedSeconds', 'isActive'];

    const diffs = [];

    for (const c of current) {
      const o = byId[String(c._id)];
      if (!o) continue;

      const payload = {};
      for (const k of keys) {
        const cv = (k === 'limitValue' || k === 'usedCount' || k === 'usedSeconds') ? numeric(c[k]) : c[k];
        const ov = (k === 'limitValue' || k === 'usedCount' || k === 'usedSeconds') ? numeric(o[k]) : o[k];
        if (cv !== ov) payload[k] = cv;
      }

      if (Object.keys(payload).length > 0) {
        diffs.push({ itemId: c._id, payload });
      }
    }
    return diffs;
  };
  // ---- data fetchers
  const fetchUsers = async () => {
    const res = await axios.get("/api/users");
    setUsers(Array.isArray(res.data) ? res.data : []);
  };
  const fetchPackages = async () => {
    const res = await axios.get("/api/packages");
    setPackages(Array.isArray(res.data.data) ? res.data.data : []);
  };
  const fetchCampaigns = async () => {
    const res = await axios.get("/api/campaigns");
    setCampaigns(Array.isArray(res.data.data) ? res.data.data : []);
  };
  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get("/api/subscriptions");
      setAbonelikler(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      console.error("Abonelikleri çekme hatası:", err);
    }
  };

  // Kota/Sayaçları çek (edit/view paneli)
  const fetchQuotaItems = async (docId) => {
    if (!docId) return [];
    setQuotaLoading(true);
    try {
      const r = await axios.get(`/api/subscriptions/${docId}/items`);
      const data = Array.isArray(r.data.data) ? r.data.data : [];
      setQuotaItems(data);
      setQuotaOriginal(JSON.parse(JSON.stringify(data))); // 🔸 derin kopya — diff için
      return data;
    } catch (e) {
      setQuotaItems([]);
      setQuotaOriginal([]);
      setSnackbar({ open: true, message: 'Kota/Sayaçlar alınamadı', severity: 'error' });
      return [];
    } finally {
      setQuotaLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPackages();
    fetchCampaigns();
    fetchSubscriptions();
  }, []);

  // ---- dialog lifecycle helpers
  const handleClose = () => {
    setViewMode(false);
    setQuotaItems([]);
    setFormData({
      avatar: '',
      fullName: '',
      subscriptions: [{ packageId: '', packageName: '', startDate: new Date().toISOString().split("T")[0], endDate: '' }],
      isActive: false,
      paymentStatus: '',
      paymentType: '',
      paymentDate: '',
      campaignUsed: false,
      campaignId: '',
      campaignName: '',
    });
  };
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  // ---- form handlers
  const handleSubscriptionChange = (index, field, value) => {
    const updated = [...formData.subscriptions];
    updated[index][field] = value;

    if (field === 'packageName') {
      const selectedPkg = packages.find(p => p.name === value);
      updated[index].packageId = selectedPkg?._id || '';
      if (selectedPkg && updated[index].startDate) {
        const start = new Date(updated[index].startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + selectedPkg.duration);
        updated[index].endDate = end.toISOString().split("T")[0];
      }
    }

    if (field === 'startDate') {
      const selectedPkg = packages.find(p => p.name === updated[index].packageName);
      if (selectedPkg) {
        const start = new Date(value);
        const end = new Date(start);
        end.setDate(start.getDate() + selectedPkg.duration);
        updated[index].endDate = end.toISOString().split("T")[0];
      }
    }

    setFormData({ ...formData, subscriptions: updated });
  };

  const addSubscriptionField = () => {
    setFormData({ ...formData, subscriptions: [...formData.subscriptions, { packageName: '', startDate: new Date().toISOString().split('T')[0], endDate: '' }] });
  };
  const removeSubscriptionField = (index) => {
    const updated = formData.subscriptions.filter((_, i) => i !== index);
    setFormData({ ...formData, subscriptions: updated });
  };

  const handleToggleStatus = async (row) => {
    try {
      await axios.put(`/api/subscriptions/${row._id}`, {
        ...row,
        isActive: !row.isActive
      });
      setSnackbar({
        open: true,
        message: `Abonelik ${row.isActive ? 'pasifleştirildi' : 'aktifleştirildi'}!`,
        severity: 'info'
      });
      fetchSubscriptions();
    } catch (err) {
      console.error('Durum değiştirme hatası:', err);
      setSnackbar({ open: true, message: 'Durum güncellenemedi!', severity: 'error' });
    }
  };

  const handleEdit = (row) => {
    setFormData({
      userId: row.userId,
      fullName: row.fullName,
      subscriptions: row.subscriptions.map(sub => ({
        ...sub,
        startDate: sub.startDate?.split("T")[0],
        endDate: sub.endDate?.split("T")[0],
      })),
      isActive: row.isActive,
      paymentStatus: row.paymentStatus,
      paymentType: row.paymentType,
      paymentDate: row.paymentDate?.split("T")[0],
      campaignUsed: !!row.campaignId,
      campaignId: row.campaignId || '',
      campaignName: row.campaignName || '',
      _id: row._id
    });
    setOpen(true);
    setViewMode(false);
    setSelectedSubDoc(row);
    fetchQuotaItems(row._id);
  };

  const handleView = (row) => {
    setFormData({
      ...row,
      subscriptions: row.subscriptions.map(sub => ({
        ...sub,
        startDate: sub.startDate?.split("T")[0],
        endDate: sub.endDate?.split("T")[0],
      })),
      paymentDate: row.paymentDate?.split("T")[0],
      campaignUsed: !!row.campaignId,
    });
    setOpen(true);
    setViewMode(true);
    setSelectedSubDoc(row);
    fetchQuotaItems(row._id);
  };

  const handleDelete = async (row) => {
    try {
      await axios.delete(`/api/subscriptions/${row._id}`);
      setSnackbar({ open: true, message: 'Abonelik silindi!', severity: 'warning' });
      fetchSubscriptions();
    } catch (err) {
      console.error("Silme hatası:", err);
      setSnackbar({ open: true, message: 'Silinemedi!', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    const hasMissingId = formData.subscriptions.some(s => !s.packageId);
    if (hasMissingId) {
      setSnackbar({ open: true, message: "Tüm paketler için packageId zorunludur.", severity: 'error' });
      return;
    }
    try {
      if (formData._id) {
        await axios.put(`/api/subscriptions/${formData._id}`, formData);
        setSnackbar({ open: true, message: 'Abonelik güncellendi!', severity: 'success' });
      } else {
        await axios.post(`/api/subscriptions`, formData);
        setSnackbar({ open: true, message: 'Abonelik eklendi!', severity: 'success' });
      }
      if (formData._id && hasQuotaChanges) {
        const diffs = buildQuotaDiffs(quotaItems, quotaOriginal);
        if (diffs.length > 0) {
          await Promise.all(
            diffs.map(d =>
              axios.patch(
                `/api/subscriptions/${formData._id}/items/${d.itemId}`,
                d.payload
              )
            )
          );
        }

        setSnackbar({ open: true, message: 'Değişiklikler kaydedildi', severity: 'success' });

        setOpen(false);
        setViewMode(false);
        setHasQuotaChanges(false);
        handleClose();
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Abonelik kaydetme hatası:', error);
      setSnackbar({ open: true, message: 'Kayıt başarısız!', severity: 'error' });
    }
  };

  // ---- filtre
  const filteredRows = abonelikler.filter(row => {
    return (
      row.subscriptions?.some(sub => (sub.packageName || '').toLowerCase().includes(searchText.toLowerCase())) &&
      (statusFilter === '' || row.isActive.toString() === statusFilter)
    );
  });

  const counts = {
    active: abonelikler.filter(a => a.isActive).length,
    passive: abonelikler.filter(a => !a.isActive).length,
    total: abonelikler.length,
  };

  // ---- yardımcılar
  const fmtRemaining = (it) => {
    if (it.limitType === 'COUNT') return `${it.remainingCount ?? 0} adet`;
    const totalSec = it.remainingSeconds ?? 0;
    const mm = Math.floor(totalSec / 60).toString().padStart(2, '0');
    const ss = (totalSec % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  // ====== ORTAK HANDLERLAR (modal + embedded) ======
  const getListPair = (which /* 'modal' | 'embedded' */) => {
    return which === 'modal'
      ? [selectedItems, setSelectedItems]
      : [quotaItems, setQuotaItems];
  };

  const resetItemCountersShared = async ({ which, docId, itemId }) => {
    const [, setList] = getListPair(which);
    try {
      await axios.post(
        `/api/subscriptions/${docId}/items/${itemId}/reset-counters`
      );
      const r = await axios.get(`/api/subscriptions/${docId}/items`);
      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setList(data);
      setSnackbar({ open: true, message: 'Sayaçlar sıfırlandı', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Sayaçlar sıfırlanamadı', severity: 'error' });
    }
  };
  // ==================================================

  return (
    <Box p={3}>
      {/* 1. Kartlar */}
      <Box display="flex" justifyContent="center" gap={2} mb={3}>
        {[{
          icon: <DoneAllIcon color="success" />, label: "Aktif Abonelik", value: counts.active
        }, {
          icon: <CancelIcon color="error" />, label: "Pasif Abonelik", value: counts.passive
        }, {
          icon: <PeopleAltIcon color="primary" />, label: "Toplam Abonelik", value: counts.total
        }].map((card, i) => (
          <Box key={i} p={2} boxShadow={2} borderRadius={2} bgcolor="white" width={250} display="flex" flexDirection="column" alignItems="center">
            {card.icon}
            <Typography variant="h6" mt={1}>{card.label}</Typography>
            <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* 2. Yeni Abonelik Ekle */}
      <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpen(true)} sx={{ mb: 2 }}>
        Yeni Abonelik Ekle
      </Button>

      {/* 3. Filtreler */}
      <Box display="flex" gap={2} mb={2}>
        <TextField label="Abonelik/Kullanıcı Ara" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Durum</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Durum">
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="true">Aktif</MenuItem>
            <MenuItem value="false">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* 4. Liste */}
      <Box sx={{ width: '97%', height: 400, mb: 2 }}>
        <DataGrid
          rows={filteredRows.map((row) => ({
            id: row._id, // id olarak _id kullan
            ...row
          }))}
          columns={[
            // KULLANICI
            {
              field: 'fullName',
              headerName: 'Kullanıcı',
              flex: 1,
              renderCell: (params) => (
                <Box display="flex" flexDirection="column" height={50} justifyContent="center">
                  <Typography variant="body2" fontWeight={600}>
                    {params.row.fullName || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {params.row.userId}
                  </Typography>
                </Box>
              )
            },

            // TÜM paketler (aktif+pasif)
            {
              field: 'packages',
              headerName: 'Tüm Paketler',
              flex: 1,
              renderCell: (params) => {
                const subs = params.row.subscriptions || [];

                if (subs.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Yok
                    </Typography>
                  );
                }

                const firstName = subs[0]?.packageName || '—';
                const extra = subs.length - 1;
                const label = extra > 0 ? `${firstName} +${extra}` : firstName;

                return (
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Box>
                        {subs.map((s) => (
                          <Typography key={s._id || s.packageName} variant="body2">
                            • {s.packageName || '—'}
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {label}
                    </Box>
                  </Tooltip>
                );
              }
            },

            // SADECE aktif paketler
            {
              field: 'activePackages',
              headerName: 'Aktif Paketler',
              flex: 1,
              renderCell: (params) => {
                const activeSubs = (params.row.subscriptions || []).filter(s => s.isActive);

                if (activeSubs.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Yok
                    </Typography>
                  );
                }

                const firstName = activeSubs[0]?.packageName || '—';
                const extra = activeSubs.length - 1;
                const label = extra > 0 ? `${firstName} +${extra}` : firstName;

                return (
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Box>
                        {activeSubs.map((s) => (
                          <Typography key={s._id || s.packageName} variant="body2">
                            • {s.packageName || '—'}
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        bgcolor: "success.main",
                        color: "success.contrastText",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {label}
                    </Box>
                  </Tooltip>
                );
              }
            },

            // GEÇERLİLİK
            {
              field: 'validity',
              headerName: 'Geçerlilik Tarihleri',
              flex: 1,
              renderCell: (params) => {
                const subs = params.row.subscriptions || [];
                if (subs.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                }

                const withDates = subs
                  .map(s => ({
                    name: s.packageName || '—',
                    startDate: s.startDate ? new Date(s.startDate) : null,
                    endDate: s.endDate ? new Date(s.endDate) : null,
                    isActive: s.isActive,
                    _id: s._id,
                  }))
                  // bitiş tarihine göre sırala, en erken biten önce gelsin
                  .sort((a, b) => {
                    if (!a.endDate && !b.endDate) return 0;
                    if (!a.endDate) return 1;
                    if (!b.endDate) return -1;
                    return a.endDate - b.endDate;
                  });

                // ekranda göstereceğimiz özet:
                // ör: "24.10.2025 → 24.11.2025 (+2)"
                const first = withDates[0];
                const extraCount = withDates.length - 1;

                const summarizeDateRange = (item) => {
                  const startStr = item.startDate
                    ? item.startDate.toLocaleDateString('tr-TR')
                    : '—';
                  const endStr = item.endDate
                    ? item.endDate.toLocaleDateString('tr-TR')
                    : '—';
                  return `${startStr} → ${endStr}`;
                };

                const chipLabel = first
                  ? extraCount > 0
                    ? `${summarizeDateRange(first)}  +${extraCount}`
                    : summarizeDateRange(first)
                  : '—';

                // tooltip içeriği: her paketin adı + tarihleri
                const tooltipContent = (
                  <Box>
                    {withDates.map(item => (
                      <Typography key={item._id || item.name} variant="body2">
                        • {item.name}: {summarizeDateRange(item)}
                        {item.isActive ? ' (aktif)' : ''}
                      </Typography>
                    ))}
                  </Box>
                );

                return (
                  <Tooltip arrow placement="top" title={tooltipContent}>
                    <Box display="inline-flex" flexDirection="column" maxWidth="100%">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          bgcolor: "grey.200",
                          color: "text.primary",
                          borderRadius: "16px",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.70rem",
                          fontWeight: 500,
                          lineHeight: 1.2,
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {chipLabel}
                      </Box>
                      {/* küçük status info, ilk paketin aktifliği */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: first?.isActive ? 'green' : 'text.secondary',
                          fontWeight: 500,
                          mt: 0.5,
                          lineHeight: 1.2,
                        }}
                      >
                      </Typography>
                    </Box>
                  </Tooltip>
                );

              }
            },

            // ABONELİK DOKÜMANI DURUMU (root.isActive)
            {
              field: 'isActive',
              headerName: 'Durum',
              flex: 0.6,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1} height={50}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: params.value ? 'green' : 'gray' }}
                  >
                    {params.value ? 'Aktif' : 'Pasif'}
                  </Typography>
                </Box>
              )
            },

            // AKTİF / PASİF BUTONU
            {
              field: 'toggle',
              headerName: 'Aktif/Pasif',
              flex: 0.6,
              renderCell: (params) => (
                <Button
                  size="small"
                  variant="outlined"
                  color={params.row.isActive ? 'warning' : 'success'}
                  onClick={() => handleToggleStatus(params.row)}
                >
                  {params.row.isActive ? 'Pasif' : 'Aktif'}
                </Button>
              )
            },

            // İŞLEMLER
            {
              field: 'actions',
              headerName: 'İşlemler',
              flex: 0.8,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1} height={50}>
                  <Tooltip title="Görüntüle">
                    <IconButton onClick={() => handleView(params.row)} size="small">
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Düzenle">
                    <IconButton onClick={() => handleEdit(params.row)} size="small">
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Sil">
                    <IconButton
                      onClick={() => handleDelete(params.row)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            }
          ]}
          pageSize={5}
        />
      </Box>

      {/* 5. Form (Preview/Edit) */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionProps={{ onExited: handleClose }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {viewMode ? "Abonelik Detayı" : formData._id ? "Aboneliği Düzenle" : "Yeni Abonelik Ekle"}
          <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* KULLANICI SEÇİMİ */}
          <FormControl fullWidth margin="dense">
            <Autocomplete
              disabled={viewMode}
              options={users.filter((u) => u.role === "user")}
              getOptionLabel={(option) => option.fullName || option.id || ""}
              onChange={(e, value) =>
                setFormData({
                  ...formData,
                  userId: value?.id || "",
                  fullName: value?.fullName || "",
                })
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Avatar
                    src={option.avatar}
                    sx={{ width: 24, height: 24, mr: 1 }}
                  />
                  {option.fullName || option.id}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kullanıcı"
                  placeholder="Ad veya soyad ara..."
                />
              )}
              value={
                users.find((u) => u.id === formData.userId) || null
              }
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
            />
          </FormControl>

          {/* PAKETLER */}
          <Typography variant="subtitle1" mt={2} mb={1}>
            Paketler
          </Typography>

          {formData.subscriptions.map((sub, index) => (
            <Box key={sub._id || index} mb={2}>
              {/* Paket seçimi */}
              <FormControl fullWidth margin="dense">
                <InputLabel>Paket Seç</InputLabel>
                <Select
                  value={sub.packageName}
                  onChange={(e) =>
                    handleSubscriptionChange(
                      index,
                      "packageName",
                      e.target.value
                    )
                  }
                  label="Paket Seç"
                  disabled={viewMode}
                >
                  {packages.map((pkg) => (
                    <MenuItem key={pkg._id} value={pkg.name}>
                      {pkg.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Tarihler + item aktif/pasif */}
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Başlangıç Tarihi"
                  type="date"
                  value={sub.startDate}
                  onChange={(e) =>
                    handleSubscriptionChange(
                      index,
                      "startDate",
                      e.target.value
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                  disabled={viewMode}
                />

                <TextField
                  label="Bitiş Tarihi"
                  type="date"
                  value={sub.endDate}
                  onChange={(e) =>
                    handleSubscriptionChange(
                      index,
                      "endDate",
                      e.target.value
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                  disabled={viewMode}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!sub.isActive}
                      disabled={viewMode}
                      onChange={(e) => {
                        const updated = [...formData.subscriptions];
                        updated[index] = {
                          ...updated[index],
                          isActive: e.target.checked,
                        };
                        setFormData({
                          ...formData,
                          subscriptions: updated,
                        });
                      }}
                    />
                  }
                  label={sub.isActive ? "Aktif" : "Pasif"}
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* ödeme geçmişi / kampanya geçmişi */}
              {Array.isArray(sub.paymentHistory) &&
                sub.paymentHistory.length > 0 && (
                  <Box
                    sx={{
                      mt: 1.5,
                      pl: 1.5,
                      borderLeft: "2px solid #ddd",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Ödeme Geçmişi / Kampanya
                    </Typography>

                    {sub.paymentHistory.map((pay, idx) => (
                      <Typography
                        key={pay._id || idx}
                        variant="caption"
                        display="block"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.4,
                        }}
                      >
                        {/* tarih varsa gösteriyoruz */}
                        {pay.createdAt
                          ? new Date(
                            pay.createdAt
                          ).toLocaleDateString("tr-TR") + " – "
                          : ""}

                        {pay.campaignName
                          ? `Kampanya: ${pay.campaignName}`
                          : "Kampanya yok"}
                      </Typography>
                    ))}
                  </Box>
                )}

              {/* paketi kaldır */}
              {!viewMode &&
                formData.subscriptions.length > 1 && (
                  <Button
                    onClick={() => removeSubscriptionField(index)}
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Paketi Kaldır
                  </Button>
                )}

              <Divider sx={{ my: 2 }} />
            </Box>
          ))}

          {/* + yeni paket ekle */}
          <Box sx={{ mt: 2 }}>

          {!viewMode && (
            <Button
              onClick={addSubscriptionField}
              variant="outlined"
              size="small"
            >
              + Yeni Paket Ekle
            </Button>
          )}

          {/* tüm abonelik kaydı aktif mi? */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                disabled={viewMode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.target.checked,
                  })
                }
              />
            }
            label={
              formData.isActive ? "Abonelik Aktif" : "Abonelik Pasif"
            }
            sx={{ display: "block"}}
          />
          </Box>

          {/* KOTA / SAYAÇLAR PANELİ */}
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Kota / Sayaçlar
            </Typography>

            {quotaLoading ? (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ py: 1 }}
              >
                <CircularProgress size={18} />{" "}
                <Typography variant="body2">
                  Yükleniyor…
                </Typography>
              </Box>
            ) : quotaItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Bu aboneliğe bağlı kota bulunamadı.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "1.2fr 0.9fr 0.8fr 0.9fr 0.9fr 1fr",
                    gap: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  <div>Paket</div>
                  <div>Tip</div>
                  <div>Limit</div>
                  <div>Kullanılan</div>
                  <div>Kalan</div>
                  <div>Durum</div>
                </Box>

                {quotaItems.map((it) => {
                  const isCount = it.limitType === "COUNT";
                  return (
                    <Box
                      key={it._id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "1.2fr 0.9fr 0.8fr 0.9fr 0.9fr 1fr",
                        gap: 1,
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {it.packageName || it.packageId}
                      </Typography>

                      <Select
                        size="small"
                        value={it.limitType}
                        disabled={viewMode}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQuotaItems((prev) =>
                            prev.map((x) =>
                              x._id === it._id
                                ? { ...x, limitType: val }
                                : x
                            )
                          );
                          setHasQuotaChanges(true);
                        }}
                      >
                        <MenuItem value="COUNT">COUNT</MenuItem>
                        <MenuItem value="MINUTES">MINUTES</MenuItem>
                      </Select>

                      <TextField
                        size="small"
                        type="number"
                        value={it.limitValue ?? 0}
                        disabled={viewMode}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQuotaItems((prev) =>
                            prev.map((x) =>
                              x._id === it._id
                                ? { ...x, limitValue: val }
                                : x
                            )
                          );
                          setHasQuotaChanges(true);
                        }}
                      />

                      {isCount ? (
                        <TextField
                          size="small"
                          type="number"
                          value={it.usedCount ?? 0}
                          disabled={viewMode}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQuotaItems((prev) =>
                              prev.map((x) =>
                                x._id === it._id
                                  ? { ...x, usedCount: val }
                                  : x
                              )
                            );
                            setHasQuotaChanges(true);
                          }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={it.usedCount ?? 0}
                          disabled={viewMode}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQuotaItems((prev) =>
                              prev.map((x) =>
                                x._id === it._id
                                  ? { ...x, usedCount: val }
                                  : x
                              )
                            );
                            setHasQuotaChanges(true);
                          }}
                        />
                      )}

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        {fmtRemaining(it)}
                      </Typography>

                      <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!it.isActive}
                              disabled={viewMode}
                              onChange={(e) => {
                                const val = e.target.checked;
                                setQuotaItems((prev) =>
                                  prev.map((x) =>
                                    x._id === it._id
                                      ? { ...x, isActive: val }
                                      : x
                                  )
                                );
                                setHasQuotaChanges(true);
                              }}
                            />
                          }
                          label={
                            it.isActive ? "Aktif" : "Pasif"
                          }
                        />
                        {!viewMode && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              resetItemCountersShared({
                                which: "embedded",
                                docId: selectedSubDoc?._id,
                                itemId: it._id,
                              })
                            }
                          >
                            Sıfırla
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </DialogContent>


        <DialogActions>
          <Button onClick={() => setOpen(false)}>Kapat</Button>
          {!viewMode && (
            <Button onClick={handleSubmit} variant="contained">Kaydet</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 7. Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionPage;
